import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import { fromBase64, type PtySession, toBase64 } from "../sprite/pty.js";
import type { SurfaceImpl } from "./surfaces.js";

/**
 * `Surface kind="terminal"` — a VT emulator, owned by the shell.
 *
 * The app holds a `stream` capability and names its id here. It never sees a
 * keystroke it did not ask for, never touches the DOM, and would render the
 * same on a platform where this is a SwiftTerm view instead. That is the whole
 * reason the terminal is a surface rather than a node type: the *protocol* says
 * "a terminal on this stream", and each renderer means it in its own idiom.
 *
 * xterm.js is a dependency, and a deliberate one. §4.1 bans UI *frameworks*,
 * because a framework's idioms leak into the protocol the SwiftUI renderer will
 * be written against. A VT100 emulator leaks nothing: it is the same category
 * of thing SwiftTerm is on the other side.
 */

/** How the surface finds the session behind a capability id. Window-scoped. */
export type StreamResolver = (capabilityId: string) => PtySession | null;

const THEME = {
  background: "#0b0d10",
  foreground: "#d7dce3",
  cursor: "#7fd1c2",
  selectionBackground: "#2a3a44",
  black: "#0b0d10",
  red: "#e0798f",
  green: "#8fd18b",
  yellow: "#e6c07b",
  blue: "#7fb3d5",
  magenta: "#c39ad5",
  cyan: "#7fd1c2",
  white: "#d7dce3",
};

export function createTerminalSurface(host: HTMLElement, resolve: StreamResolver): SurfaceImpl {
  const term = new Terminal({
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
    fontSize: 12,
    lineHeight: 1.2,
    theme: THEME,
    cursorBlink: true,
    scrollback: 5000,
  });
  const fit = new FitAddon();
  term.loadAddon(fit);
  term.open(host);

  let session: PtySession | null = null;
  let boundTo: string | null = null;
  let unsubscribe: (() => void) | null = null;
  let unclose: (() => void) | null = null;

  // Streaming, because a PTY chunk boundary lands wherever the kernel put it —
  // straight through the middle of a multi-byte character often enough that a
  // non-streaming decoder turns box-drawing output into replacement glyphs.
  const decoder = new TextDecoder("utf-8");
  const encoder = new TextEncoder();

  // Keystrokes go to the session, which the app granted us by holding it. The
  // app is not in this path: a terminal the user types into does not round-trip
  // through the app process to echo a character.
  term.onData((data: string) => session?.write(toBase64(encoder.encode(data))));

  const resize = (): void => {
    if (host.clientWidth === 0 || host.clientHeight === 0) return;
    try {
      fit.fit();
    } catch {
      // Not laid out yet; the observer will call again when it is.
      return;
    }
    session?.resize(term.cols, term.rows);
  };

  const observer = new ResizeObserver(() => resize());
  observer.observe(host);

  const detach = (): void => {
    unsubscribe?.();
    unclose?.();
    unsubscribe = null;
    unclose = null;
    session = null;
    boundTo = null;
  };

  const bind = (capabilityId: string): void => {
    if (boundTo === capabilityId) return;
    detach();
    const found = resolve(capabilityId);
    if (found === null) {
      term.write("\r\n\x1b[2m…waiting for a stream\x1b[0m\r\n");
      return;
    }
    boundTo = capabilityId;
    session = found;
    unsubscribe = found.subscribe((data) =>
      term.write(decoder.decode(fromBase64(data), { stream: true })),
    );
    unclose = found.onClose((reason) => {
      term.write(`\r\n\x1b[2m${reason}\x1b[0m\r\n`);
      detach();
    });
    resize();
  };

  return {
    update(props: Record<string, unknown>): void {
      const stream = props["stream"];
      if (typeof stream === "string" && stream !== "") bind(stream);
      else detach();
    },
    destroy(): void {
      observer.disconnect();
      detach();
      term.dispose();
    },
  };
}
